import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { EFFECTIVE_DATE, COMPANY_LEGAL, COMPANY_ABN, ADDRESS, CONTACT_EMAIL } from '@/lib/constants'

const sections = [
  {
    id: 'who-we-are',
    title: '1. Who We Are',
    body: `${COMPANY_LEGAL} ("custum.ai", "we", "us", or "our") is the data controller responsible for your personal data. We provide AI-powered business solutions including AI Caller, AI Receptionist, and AI Webchat services.

If you have any questions about this Privacy Policy or how we handle your personal data, you can contact our privacy team at ${CONTACT_EMAIL}.`,
  },
  {
    id: 'what-we-collect',
    title: '2. What Personal Data We Collect',
    body: `We collect and process the following categories of personal data:

Information you provide directly
• Contact and enquiry data: name, email address, phone number, company name, and any information included in messages you send via our contact forms or email
• Account data: login credentials and preferences if you have a client account
• Communication records: records of correspondence between you and our team

Information collected automatically
• Usage data: pages visited, time on site, referring URLs, browser type, device type, and IP address
• Cookie data: session identifiers and analytics identifiers (see Section 7 on Cookies)

Data provided by our clients (end-user data)
If you are a business client, you may provide us with data about your own customers (e.g., call recordings, chat transcripts, CRM records) for the purpose of training and operating AI agents. We act as a data processor for this data, and you remain the data controller. Processing of this data is governed by a separate Data Processing Agreement.`,
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Personal Data',
    body: `We use your personal data for the following purposes and on the following legal bases:

Purpose | Legal Basis
To respond to your enquiries and provide our services | Performance of a contract / Pre-contractual steps
To manage your client account | Performance of a contract
To send service-related communications (invoices, updates, alerts) | Performance of a contract
To improve our website and services through analytics | Legitimate interests
To comply with legal obligations (tax, regulatory reporting) | Legal obligation
To send marketing communications about our services | Consent (you may withdraw at any time)

We will never sell your personal data to third parties, and we do not use client business data or end-user conversation data to train general-purpose or third-party AI models.`,
  },
  {
    id: 'who-we-share',
    title: '4. Who We Share Your Data With',
    body: `We may share your personal data with the following categories of recipients only where necessary to provide our services:

• Service providers: cloud hosting providers, CRM platforms, payment processors, and IT support partners — all operating under data processing agreements with equivalent data protection obligations
• AI infrastructure providers: large language model API providers used to power our AI agents (data is processed in accordance with their enterprise data protection terms)
• CRM integrations: systems such as HubSpot, Salesforce, and Zoho CRM, where you have authorised the integration as part of your service configuration
• Professional advisers: lawyers, accountants, and auditors bound by confidentiality obligations
• Law enforcement or regulators: where required by applicable law or to protect our legal rights

We do not share your data with any other third parties without your explicit consent.`,
  },
  {
    id: 'international',
    title: '5. International Data Transfers',
    body: `Some of our service providers are located outside the United Kingdom and the European Economic Area. Where we transfer personal data to countries that do not offer an equivalent level of data protection, we ensure appropriate safeguards are in place, including:

• Standard Contractual Clauses (SCCs) approved by the UK Information Commissioner's Office or the European Commission
• Adequacy decisions where applicable
• Binding corporate rules where applicable

You may request further information about the specific safeguards in place for any international transfer by contacting us at ${CONTACT_EMAIL}.`,
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    body: `We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, including legal, accounting, or reporting requirements.

• Enquiry and contact data: 2 years from last contact, or for the duration of the client relationship if a contract is established
• Client account and contract data: 7 years from end of contract (to comply with financial record-keeping requirements)
• Website analytics data: 26 months in anonymised or aggregated form
• Call recordings and chat transcripts (client end-user data): as specified in your Data Processing Agreement, typically 90 days unless extended by client instruction

When data is no longer required, it is securely deleted or anonymised.`,
  },
  {
    id: 'cookies',
    title: '7. Cookies',
    body: `Our website uses cookies and similar tracking technologies. We use:

Essential cookies — required for the website to function (session management, security). These cannot be disabled.

Analytics cookies — help us understand how visitors use our site (page views, traffic sources). We use anonymised analytics and do not track individual users across third-party sites.

We do not use advertising or retargeting cookies.

You can control cookie preferences through your browser settings. Disabling analytics cookies will not affect your ability to use the site.`,
  },
  {
    id: 'security',
    title: '8. Data Security',
    body: `We take the security of your personal data seriously and implement appropriate technical and organisational measures to protect it against unauthorised access, disclosure, alteration, or destruction. Our security measures include:

• End-to-end encryption for all data in transit (TLS 1.2+) and at rest (AES-256)
• Role-based access controls and principle of least privilege
• Regular security assessments and penetration testing
• Staff training on data protection and information security
• Incident response procedures with notification obligations

In the event of a data breach likely to result in risk to your rights and freedoms, we will notify the ICO within 72 hours and affected individuals without undue delay as required by applicable law.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    body: `Our services are not directed at children under the age of 16 and we do not knowingly collect personal data from children. If we become aware that we have inadvertently collected personal data from a child, we will delete it promptly. If you believe we may have collected data from a child, please contact us at ${CONTACT_EMAIL}.`,
  },
  {
    id: 'changes',
    title: '10. Changes to This Privacy Policy',
    body: `We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. We will update the effective date at the top of this page when we make changes.

For material changes, we will provide more prominent notice — for example, by email to active clients or a notice on our website. We encourage you to review this page periodically.`,
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    body: `If you have any questions, concerns, or requests relating to this Privacy Policy or our data practices, please contact us:

Email: ${CONTACT_EMAIL}
Post: ${COMPANY_LEGAL} (ABN ${COMPANY_ABN}), Data Protection, ${ADDRESS}

We aim to respond to all privacy-related enquiries within 5 business days.`,
  },
];

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="pt-24 min-h-screen bg-brand-black">
      <Helmet>
        <title>Privacy Policy — custum.ai</title>
        <meta name="description" content="Privacy Policy for custum.ai — how we collect, use, and protect your personal data." />
        <link rel="canonical" href="https://custum.ai/privacy/" />
      </Helmet>

      {/* Header */}
      <section className="py-16 md:py-24 bg-brand-charcoal" style={{ borderBottom: '1px solid var(--brand-border)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
              Legal
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight mb-4">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-sm text-brand-muted">
              Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp; {COMPANY_LEGAL}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">

            {/* Sticky TOC */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-32">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-muted mb-4">Contents</p>
                <nav className="space-y-2">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-xs text-brand-muted hover:text-brand-white transition-colors leading-relaxed"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Body */}
            <div className="lg:col-span-3 space-y-12">
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-sm text-brand-muted leading-relaxed"
              >
                At custum.ai we are committed to protecting your privacy. This Privacy Policy explains what personal data we collect, why we collect it, how we use it, and what rights you have. It applies to all visitors to our website and users of our services.
              </motion.p>

              {sections.map((section, idx) => (
                <motion.div
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.4, delay: idx * 0.03 }}
                >
                  <h2 className="text-lg font-bold text-brand-white mb-4 pb-3" style={{ borderBottom: '1px solid var(--brand-border)' }}>
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.body.split('\n\n').map((para, i) => (
                      <p key={i} className="text-sm text-brand-muted leading-relaxed whitespace-pre-line">
                        {para}
                      </p>
                    ))}
                  </div>
                </motion.div>
              ))}

              <div className="pt-8 mt-8" style={{ borderTop: '1px solid var(--brand-border)' }}>
                <p className="text-xs text-brand-muted">
                  Privacy enquiries:{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-blue-light hover:text-brand-white transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
